/**
 * Dashboard Component
 *
 * Main hub for authenticated users. Owns flow + project state and the
 * top-level routing between the projects-grid view and the flows-in-a-
 * project view.
 *
 * Projects v1 is gated behind `isProjectsV1Enabled()`. When OFF, this
 * file behaves exactly like the pre-projects refactor: a single Knods
 * grid + the Tutorials panel. When ON, the Tutorials panel is hidden
 * entirely and the dashboard uses `?project=<id|inbox>` to switch
 * between the grid of project cards and the grid of Knods inside the
 * selected project.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useFlowLimit } from '../hooks/useFlowLimit';
import { reportFlowLimitRefusal } from '../utils/flowLimit';
import supabaseFlowStorageService from '../services/SupabaseFlowStorageService';
import flowSerializationService from '../services/FlowSerializationService';
import projectStorageService from '../services/ProjectStorageService';
import { getCurrentUserId, peekCurrentUserId } from '../lib/currentUser';
import { isProjectsV1Enabled } from '../utils/projectsFlag';
import type { SavedFlow } from '../types/flow';
import type { Project } from '../types/project';
import { INBOX_PROJECT_ID } from '../types/project';
import {
  YouTubeLightbox,
  FlowGrid,
  TutorialsPanel,
  DEFAULT_TUTORIAL_VIDEOS,
  ProjectGrid,
  ProjectModal,
  MoveToProjectModal,
  type TutorialVideo,
  type ProjectModalInput,
} from './dashboard/index';
// Imported directly rather than through the dashboard barrel so a test can stub
// the heavy grids without also having to stub the counter it is asserting on.
import FlowUsageCounter from './dashboard/FlowUsageCounter';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './Dashboard.css';
import UserMenu from './UserMenu';

// Logo component
const KnodsLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 66 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.51918 63.0499C3.8147 63.0499 0 66.8448 0 71.5249C0 76.2051 3.8147 80 8.51918 80C13.2237 80 17.0384 76.2051 17.0384 71.5249C17.0384 66.8448 13.2237 63.0499 8.51918 63.0499ZM8.51918 75.7625C6.16694 75.7625 4.25959 73.865 4.25959 71.5249C4.25959 69.1849 6.16694 67.2874 8.51918 67.2874C10.8714 67.2874 12.7788 69.1849 12.7788 71.5249C12.7788 73.865 10.8714 75.7625 8.51918 75.7625Z" fill="#FDD405"/>
    <path d="M27.5217 39.1501C26.064 38.7405 24.9281 37.5728 24.5589 36.1038L12.9397 47.3427C11.6476 46.5611 10.1378 46.0997 8.51918 46.0997C3.8147 46.0997 0 49.8947 0 54.5748C0 59.2549 3.8147 63.0499 8.51918 63.0499C13.2237 63.0499 17.0384 59.2549 17.0384 54.5748C17.0384 53.0446 16.6266 51.6132 15.9119 50.3749L27.5217 39.1454V39.1501ZM8.51918 58.8123C6.16694 58.8123 4.25959 56.9148 4.25959 54.5748C4.25959 52.2347 6.16694 50.3372 8.51918 50.3372C10.8714 50.3372 12.7788 52.2347 12.7788 54.5748C12.7788 56.9148 10.8714 58.8123 8.51918 58.8123Z" fill="#42C3D7"/>
    <path d="M6.38939 16.6724V46.3775C7.07092 46.2033 7.78085 46.0997 8.51918 46.0997C9.25751 46.0997 9.96744 46.2033 10.649 46.3775V16.6724C14.3217 15.7307 17.0384 12.4207 17.0384 8.47508C17.0384 3.79495 13.2237 0 8.51918 0C3.8147 0 0 3.79495 0 8.47508C0 12.4207 2.71667 15.7307 6.38939 16.6724ZM8.51918 4.23754C10.8714 4.23754 12.7788 6.13501 12.7788 8.47508C12.7788 10.8151 10.8714 12.7126 8.51918 12.7126C6.16694 12.7126 4.25959 10.8151 4.25959 8.47508C4.25959 6.13501 6.16694 4.23754 8.51918 4.23754Z" fill="#FDD405"/>
    <path d="M56.1745 0C51.4701 0 47.6554 3.79495 47.6554 8.47508C47.6554 10.0053 48.0671 11.4366 48.7818 12.6749L29.8408 30.9999C31.3033 31.4049 32.4392 32.5678 32.8131 34.0321L51.754 15.7071C53.0461 16.4887 54.5559 16.9502 56.1745 16.9502C60.879 16.9502 64.6937 13.1552 64.6937 8.47508C64.6937 3.79495 60.879 0 56.1745 0ZM56.1745 12.7126C53.8223 12.7126 51.915 10.8151 51.915 8.47508C51.915 6.13501 53.8223 4.23754 56.1745 4.23754C58.5268 4.23754 60.4341 6.13501 60.4341 8.47508C60.4341 10.8151 58.5268 12.7126 56.1745 12.7126Z" fill="#42C3D7"/>
    <path d="M57.4808 63.0499C56.2645 63.0499 55.1096 63.3088 54.0589 63.7655L35.454 40.2095C36.5473 38.7829 37.2099 37.0078 37.2099 35.0727C37.2099 30.3926 33.3952 26.5976 28.6907 26.5976C23.9862 26.5976 20.1715 30.3926 20.1715 35.0727C20.1715 39.7528 23.9862 43.5478 28.6907 43.5478C29.9071 43.5478 31.0619 43.2888 32.1126 42.8321L50.7175 66.3881C49.6195 67.8147 48.9616 69.5898 48.9616 71.5249C48.9616 76.2051 52.7763 80 57.4808 80C62.1853 80 66 76.2051 66 71.5249C66 66.8448 62.1853 63.0499 57.4808 63.0499ZM28.686 39.3102C26.3337 39.3102 24.4264 37.4127 24.4264 35.0727C24.4264 32.7326 26.3337 30.8351 28.686 30.8351C31.0382 30.8351 32.9456 32.7326 32.9456 35.0727C32.9456 37.4127 31.0382 39.3102 28.686 39.3102ZM57.4808 75.7625C55.1286 75.7625 53.2212 73.865 53.2212 71.5249C53.2212 69.1849 55.1286 67.2874 57.4808 67.2874C59.8331 67.2874 61.7404 69.1849 61.7404 71.5249C61.7404 73.865 59.8331 75.7625 57.4808 75.7625Z" fill="#F04E47"/>
  </svg>
);

// Project metadata used in the projects-grid view to compute counts and covers.
// Kept as a memoized derivation rather than persisted state.
interface ProjectGridDerivation {
  counts: Record<string, number>;
  covers: Record<string, string | undefined>;
}

// ── Cross-mount data snapshot ──────────────────────────────────────
// The Dashboard unmounts every time the user opens a knod and mounts again
// when they come back, so without this each return trip restarted the fetch
// from cold and the user stared at an empty grid until it finished. Holding
// the last result in module scope — it has to outlive the component — lets the
// return trip paint immediately and revalidate behind the paint.
//
// Keyed by user id so a second account can never inherit the first one's
// knods; an unresolved id (cold boot) simply declines the warm start.
interface DashboardSnapshot {
  userId: string;
  flows: SavedFlow[];
  projects: Project[];
}

let dashboardSnapshot: DashboardSnapshot | null = null;

function readDashboardSnapshot(): DashboardSnapshot | null {
  const userId = peekCurrentUserId();
  if (!userId || dashboardSnapshot?.userId !== userId) return null;
  return dashboardSnapshot;
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  useAuth(); // Ensure auth context is initialized
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();

  // Projects v1 — read at mount time; toggling the flag in localStorage
  // requires a reload, which is fine for a dev/QA flag.
  const projectsEnabled = useMemo(() => isProjectsV1Enabled(), []);

  // ── Selected project from URL ─────────────────────────────────────
  // null    → projects grid view (default)
  // 'inbox' → Inbox flows
  // <uuid>  → that project's flows
  const selectedProjectParam = projectsEnabled ? (searchParams.get('project') || null) : null;

  // Flow + project data state. A warm start means we already have this
  // account's last-known data in memory: render it now, refresh underneath.
  const [warmStart] = useState(readDashboardSnapshot);
  const [flows, setFlows] = useState<SavedFlow[]>(() => warmStart?.flows ?? []);
  const [projects, setProjects] = useState<Project[]>(() => warmStart?.projects ?? []);
  const [loading, setLoading] = useState(!warmStart);
  const [showSkeletons, setShowSkeletons] = useState(!warmStart);

  // ── Plan's knod cap ──────────────────────────────────────────────
  // Only the cap comes from the server. The *count* is `flows.length`, which is
  // the unfiltered list (archived included — exactly what the migration-099
  // trigger counts), so the counter reacts to a create or a delete immediately
  // instead of waiting for a refetch. The server's `used` is the fallback for
  // the window before the listing lands.
  const { limit: flowLimit, used: serverFlowCount, loading: flowLimitLoading } = useFlowLimit();
  const flowCount = loading ? serverFlowCount : flows.length;

  // ── One refusal handler for every creation path ──────────────────
  // The cap is enforced by a BEFORE INSERT trigger, so every path that creates
  // a knod can be turned down the same way and must explain it the same way.
  // Returns true when it handled the error, so the caller can skip its generic
  // fallback — including any retry, which for this error can only fail again.
  const explainIfFlowLimit = useCallback(
    (error: unknown): boolean =>
      reportFlowLimitRefusal(error, {
        used: flows.length,
        onUpgrade: () => navigate('/pricing'),
      }),
    [flows.length, navigate],
  );

  // Bumped by every local edit. A load that started before an edit and lands
  // after it is discarded, so a background revalidation can't resurrect a
  // just-deleted knod or undo a just-applied rename.
  const mutationGenerationRef = useRef(0);
  const bumpMutationGeneration = useCallback(() => {
    mutationGenerationRef.current += 1;
  }, []);

  // The account the current snapshot belongs to. Seeded from a warm start and
  // refreshed by each successful load, so edits are never mirrored into
  // another account's snapshot after a cross-tab sign-in.
  const snapshotUserIdRef = useRef<string | null>(warmStart?.userId ?? null);

  // Set once this instance is gone. Covers the module-scope snapshot, which
  // React's unmounted-setState handling does not: a load resolving after the
  // user has navigated away must not publish its result for the next visit.
  const cancelledRef = useRef(false);

  // Filter / sort / view UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<TutorialVideo | null>(null);
  const [mounted, setMounted] = useState(false);
  const [mobileBannerDismissed, setMobileBannerDismissed] = useState(() =>
    localStorage.getItem('knods-mobile-banner-dismissed') === 'true'
  );

  // Flow modals
  const [deleteConfirm, setDeleteConfirm] = useState<SavedFlow | null>(null);
  const [renameFlow, setRenameFlow] = useState<SavedFlow | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Project modals
  const [projectModalMode, setProjectModalMode] = useState<'create' | 'rename' | null>(null);
  const [projectModalInitial, setProjectModalInitial] = useState<ProjectModalInput | undefined>(undefined);
  const [projectBeingEdited, setProjectBeingEdited] = useState<Project | null>(null);
  const [deleteProjectConfirm, setDeleteProjectConfirm] = useState<Project | null>(null);
  const [moveTargetFlow, setMoveTargetFlow] = useState<SavedFlow | null>(null);
  // Bulk-move modal — separate from single-flow move so the two paths
  // don't fight over the same modal state.
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);

  // Multi-select state. Cleared whenever the project changes (so the
  // user doesn't carry hidden selections across projects) and on first
  // mount.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedFlowIds, setSelectedFlowIds] = useState<Set<string>>(() => new Set());

  // Sort
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const saved = localStorage.getItem('dashboard-sort-order');
    return (saved === 'asc' || saved === 'desc') ? saved : 'desc';
  });
  const [sortBy, setSortBy] = useState<'created' | 'updated'>(() => {
    const saved = localStorage.getItem('dashboard-sort-by');
    return (saved === 'created' || saved === 'updated') ? saved : 'created';
  });

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    if (isMobile && viewMode !== 'grid') setViewMode('grid');
  }, [isMobile, viewMode]);

  useEffect(() => { localStorage.setItem('dashboard-sort-order', sortOrder); }, [sortOrder]);
  useEffect(() => { localStorage.setItem('dashboard-sort-by', sortBy); }, [sortBy]);

  // ── Load flows + (optionally) projects ───────────────────────────
  // `background: true` revalidates without tearing down what's on screen —
  // used on a warm start, where showing skeletons over correct data would be
  // a step backwards.
  const loadData = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
      setShowSkeletons(true);
    }
    // A background revalidation runs alongside a usable grid, so the user can
    // delete or rename while it is still in flight. Anything they change bumps
    // this counter, and a load whose counter moved underneath it drops its
    // result rather than resurrecting what they just removed.
    const generation = mutationGenerationRef.current;
    try {
      const [loadedFlows, loadedProjects] = await Promise.all([
        supabaseFlowStorageService.listFlows(),
        projectsEnabled ? projectStorageService.listProjects() : Promise.resolve([] as Project[]),
      ]);

      // Drop a load the user has already overtaken. Their own edit is applied
      // to local state by the handler that made it, so the view stays correct
      // for what they changed; anything else this load would have brought is
      // picked up on the next visit. Deliberately no immediate refetch — one
      // would race the very mutation that invalidated this load.
      if (mutationGenerationRef.current !== generation || cancelledRef.current) {
        return;
      }

      const sortedFlows = loadedFlows.sort((a, b) =>
        new Date(b.metadata.updatedAt).getTime() - new Date(a.metadata.updatedAt).getTime()
      );
      setFlows(sortedFlows);
      setProjects(loadedProjects);

      // Only a load that actually succeeded may seed the cross-mount snapshot,
      // and it is stamped with the account it was fetched under rather than
      // with whoever is signed in by the time it lands — a sign-in in another
      // tab can change that mid-flight.
      const userId = await getCurrentUserId();
      if (userId && mutationGenerationRef.current === generation && !cancelledRef.current) {
        snapshotUserIdRef.current = userId;
        dashboardSnapshot = { userId, flows: sortedFlows, projects: loadedProjects };
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      // Deliberately no snapshot write. Caching a failed load would replay
      // "this account has no knods" on the next visit — instantly, with no
      // skeletons and no error, which reads as data loss.
    } finally {
      setLoading(false);
      // Tail matches the skeleton overlay's fade-out; the real grid is already
      // painted underneath by this point, so it delays nothing.
      setTimeout(() => setShowSkeletons(false), 400);
    }
  }, [projectsEnabled]);

  useEffect(() => {
    cancelledRef.current = false;
    loadData({ background: warmStart !== null });
    return () => { cancelledRef.current = true; };
  }, [loadData, warmStart]);

  // Keep the snapshot in step with local edits (rename, archive, move, delete)
  // so the next visit starts from the current view rather than a pre-edit copy.
  // Only ever updates a snapshot a successful load already established, and
  // only under the account that load ran as.
  useEffect(() => {
    const userId = snapshotUserIdRef.current;
    if (!userId || dashboardSnapshot?.userId !== userId) return;
    dashboardSnapshot = { userId, flows, projects };
  }, [flows, projects]);

  // ── Create a new flow, optionally pre-assigned to a project ──────
  // When viewing a project, the new flow inherits its projectId (Inbox
  // is implicit-null). When on the projects grid, projectId is null →
  // Inbox.
  const handleCreateNewKnod = async () => {
    // An insert invalidates any listing already in flight — better to discard
    // that load than to cache a list predating the new knod.
    bumpMutationGeneration();
    try {
      const now = new Date();
      const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      const flowName = `Knod - ${date} at ${time}`;

      const newFlow = flowSerializationService.serializeFlow(
        [], [], { x: 0, y: 0, zoom: 1 }, { name: flowName }, false,
      );

      // Inherit the current project context (if any). Inbox = null.
      if (projectsEnabled && selectedProjectParam && selectedProjectParam !== INBOX_PROJECT_ID) {
        newFlow.metadata.projectId = selectedProjectParam;
      } else {
        newFlow.metadata.projectId = null;
      }

      await supabaseFlowStorageService.saveFlow(newFlow);
      navigate(`/knod/${newFlow.metadata.id}`);
    } catch (error) {
      console.error('[Dashboard] Failed to create new knod:', error);
      // The /flow fallback below re-attempts the very same insert in the
      // editor, so for a limit refusal it would fail a second time and strand
      // the user on a blank canvas. Explain it and stay put instead.
      if (explainIfFlowLimit(error)) return;
      navigate('/flow');
    }
  };

  const handleStartTutorial = async () => {
    bumpMutationGeneration();
    try {
      const now = new Date();
      const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      const flowName = `Knod - ${date} at ${time}`;
      const newFlow = flowSerializationService.serializeFlow(
        [], [], { x: 0, y: 0, zoom: 1 }, { name: flowName }, false,
      );
      await supabaseFlowStorageService.saveFlow(newFlow);
      navigate(`/knod/${newFlow.metadata.id}?tutorial=first-image`);
    } catch (error) {
      console.error('[Dashboard] Failed to start tutorial:', error);
      // Same reasoning as handleCreateNewKnod: the fallback would just retry.
      if (explainIfFlowLimit(error)) return;
      navigate('/flow?tutorial=first-image');
    }
  };

  // ── Flow actions ────────────────────────────────────────────────
  const handleOpenKnod = (flow: SavedFlow) => navigate(`/knod/${flow.metadata.id}`);

  const handleCloneKnod = async (flow: SavedFlow) => {
    bumpMutationGeneration();
    try {
      // The grid holds summaries — no nodes, no edges — so the graph has to be
      // fetched before it can be copied. Cloning `flow.state` directly would
      // produce an empty knod.
      const source = await supabaseFlowStorageService.loadFlow(flow.metadata.id);
      if (!source) {
        console.error('Failed to clone knod: source flow not found', flow.metadata.id);
        return;
      }

      const newFlowId = crypto.randomUUID();
      await supabaseFlowStorageService.saveFlow({
        state: source.state,
        metadata: {
          ...source.metadata,
          id: newFlowId,
          name: `${flow.metadata.name} (Copy)`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        isTemplate: false,
      });
      navigate(`/knod/${newFlowId}`);
    } catch (error) {
      console.error('Failed to clone knod:', error);
      // A duplicate is a new row, so it is capped like any other create — and
      // this handler used to swallow the refusal entirely.
      if (explainIfFlowLimit(error)) return;
    }
  };

  const handleDeleteFlow = (flow: SavedFlow) => setDeleteConfirm(flow);

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    bumpMutationGeneration();
    try {
      await supabaseFlowStorageService.deleteFlow(deleteConfirm.metadata.id);
      // Functional update: a background revalidation can land during the await
      // above, so `flows` from this closure may already be stale.
      setFlows(prev => prev.filter(f => f.metadata.id !== deleteConfirm.metadata.id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete flow:', error);
    }
  };

  const handleRenameFlow = (flow: SavedFlow) => {
    setRenameFlow(flow);
    setRenameValue(flow.metadata.name);
  };

  // Rename and archive both patch a single column rather than round-tripping
  // through saveFlow: the grid's flows are summaries with an empty `state`, so
  // a full save would overwrite the stored graph with nothing.
  const confirmRename = async () => {
    if (!renameFlow || !renameValue.trim()) return;
    bumpMutationGeneration();
    const name = renameValue.trim();
    try {
      const updatedAt = await supabaseFlowStorageService.renameFlow(renameFlow.metadata.id, name);
      setFlows(prev => prev.map(f => f.metadata.id === renameFlow.metadata.id
        ? { ...f, metadata: { ...f.metadata, name, updatedAt } }
        : f));
      setRenameFlow(null);
      setRenameValue('');
    } catch (error) {
      console.error('Failed to rename flow:', error);
    }
  };

  const handleArchiveFlow = async (flow: SavedFlow) => {
    const archived = !flow.metadata.archived;
    bumpMutationGeneration();
    try {
      const updatedAt = await supabaseFlowStorageService.setFlowArchived(flow.metadata.id, archived);
      setFlows(prev => prev.map(f => f.metadata.id === flow.metadata.id
        ? { ...f, metadata: { ...f.metadata, archived, updatedAt } }
        : f));
    } catch (error) {
      console.error('Failed to archive flow:', error);
    }
  };

  // ── Project actions ─────────────────────────────────────────────
  const openCreateProjectModal = () => {
    setProjectBeingEdited(null);
    setProjectModalInitial(undefined);
    setProjectModalMode('create');
  };

  const openRenameProjectModal = (project: Project) => {
    setProjectBeingEdited(project);
    setProjectModalInitial({ name: project.name, color: project.color, emoji: project.emoji });
    setProjectModalMode('rename');
  };

  const handleProjectModalSubmit = async (input: ProjectModalInput) => {
    bumpMutationGeneration();
    try {
      if (projectModalMode === 'create') {
        const created = await projectStorageService.createProject(input);
        setProjects((prev) => [...prev, created]);
      } else if (projectModalMode === 'rename' && projectBeingEdited) {
        const updated = await projectStorageService.updateProject(projectBeingEdited.id, input);
        setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      }
    } catch (e) {
      console.error('[Dashboard] project modal save failed:', e);
    } finally {
      setProjectModalMode(null);
      setProjectBeingEdited(null);
      setProjectModalInitial(undefined);
    }
  };

  const handleArchiveProject = async (project: Project) => {
    bumpMutationGeneration();
    try {
      const updated = await projectStorageService.updateProject(project.id, { archived: !project.archived });
      setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (e) {
      console.error('[Dashboard] archive project failed:', e);
    }
  };

  const confirmDeleteProject = async () => {
    if (!deleteProjectConfirm) return;
    bumpMutationGeneration();
    try {
      await projectStorageService.deleteProject(deleteProjectConfirm.id);
      setProjects((prev) => prev.filter((p) => p.id !== deleteProjectConfirm.id));
      // ON DELETE SET NULL on flows.project_id means deleted-project flows
      // fall back to Inbox automatically server-side. Reflect that locally
      // so we don't have to refetch every flow.
      setFlows((prev) => prev.map((f) =>
        f.metadata.projectId === deleteProjectConfirm.id
          ? { ...f, metadata: { ...f.metadata, projectId: null } }
          : f
      ));
      setDeleteProjectConfirm(null);
    } catch (e) {
      console.error('[Dashboard] delete project failed:', e);
    }
  };

  const handleMoveSelect = async (target: string | typeof INBOX_PROJECT_ID) => {
    if (!moveTargetFlow) return;
    bumpMutationGeneration();
    try {
      const newProjectId = target === INBOX_PROJECT_ID ? null : target;
      await projectStorageService.moveFlowToProject(moveTargetFlow.metadata.id, target);
      setFlows((prev) => prev.map((f) =>
        f.metadata.id === moveTargetFlow.metadata.id
          ? { ...f, metadata: { ...f.metadata, projectId: newProjectId } }
          : f
      ));
    } catch (e) {
      console.error('[Dashboard] move flow failed:', e);
    } finally {
      setMoveTargetFlow(null);
    }
  };

  // ── Multi-select handlers ───────────────────────────────────────
  const handleEnterSelection = useCallback(() => {
    setSelectionMode(true);
    setSelectedFlowIds(new Set());
  }, []);

  const handleExitSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedFlowIds(new Set());
  }, []);

  const handleToggleSelect = useCallback((flow: SavedFlow) => {
    setSelectedFlowIds((prev) => {
      const next = new Set(prev);
      if (next.has(flow.metadata.id)) next.delete(flow.metadata.id);
      else next.add(flow.metadata.id);
      return next;
    });
  }, []);

  const handleBulkMoveSelect = async (target: string | typeof INBOX_PROJECT_ID) => {
    if (selectedFlowIds.size === 0) {
      setBulkMoveOpen(false);
      return;
    }
    bumpMutationGeneration();
    try {
      const ids = Array.from(selectedFlowIds);
      const newProjectId = target === INBOX_PROJECT_ID ? null : target;
      await projectStorageService.bulkMoveFlowsToProject(ids, target);
      // Reflect the move locally so we don't refetch all flows.
      setFlows((prev) => prev.map((f) =>
        ids.includes(f.metadata.id)
          ? { ...f, metadata: { ...f.metadata, projectId: newProjectId } }
          : f
      ));
    } catch (e) {
      console.error('[Dashboard] bulk move failed:', e);
    } finally {
      setBulkMoveOpen(false);
      setSelectionMode(false);
      setSelectedFlowIds(new Set());
    }
  };

  // Selection is per-view: switching projects clears it so the user
  // doesn't carry invisible selections around. The archived toggle is
  // also per-view — each project (and the global view) starts on its
  // active knods, and the user opts into archived from within that view.
  useEffect(() => {
    setSelectionMode(false);
    setSelectedFlowIds(new Set());
    setShowArchived(false);
  }, [selectedProjectParam]);

  // ── Derived data ────────────────────────────────────────────────
  // Counts and covers for the projects grid, keyed by project id +
  // INBOX_PROJECT_ID.
  const projectGridDerivation: ProjectGridDerivation = useMemo(() => {
    const counts: Record<string, number> = {};
    const covers: Record<string, string | undefined> = {};

    // Iterate flows in already-sorted (most-recent-first) order so the
    // first flow we touch in each bucket becomes the cover.
    for (const f of flows) {
      if (f.metadata.archived) continue;
      const key = f.metadata.projectId ?? INBOX_PROJECT_ID;
      counts[key] = (counts[key] ?? 0) + 1;
      if (!covers[key] && f.metadata.thumbnail) covers[key] = f.metadata.thumbnail;
    }
    return { counts, covers };
  }, [flows]);

  // Flows to show in the FlowGrid: filtered by selected project (when
  // projects-on), search query, archived toggle, and sorted.
  //
  // Archived scoping: the project/Inbox filter runs FIRST, so when the
  // archived toggle is on inside a project view we only surface that
  // project's archived knods (archived rows keep their projectId — see
  // FlowSerializationService + Dashboard archive handler). This makes
  // archived knods reachable under their own project without leaking
  // other projects' archived knods into the view.
  const filteredFlows = useMemo(() => {
    let result = flows;

    if (projectsEnabled && selectedProjectParam) {
      if (selectedProjectParam === INBOX_PROJECT_ID) {
        result = result.filter((f) => (f.metadata.projectId ?? null) === null);
      } else {
        result = result.filter((f) => f.metadata.projectId === selectedProjectParam);
      }
    }

    return result
      .filter((flow) => {
        const matchesSearch = flow.metadata.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesArchived = showArchived ? !!flow.metadata.archived : !flow.metadata.archived;
        return matchesSearch && matchesArchived;
      })
      .sort((a, b) => {
        const da = new Date(sortBy === 'created' ? a.metadata.createdAt : a.metadata.updatedAt).getTime();
        const db = new Date(sortBy === 'created' ? b.metadata.createdAt : b.metadata.updatedAt).getTime();
        return sortOrder === 'desc' ? db - da : da - db;
      });
  }, [flows, projectsEnabled, selectedProjectParam, searchQuery, showArchived, sortBy, sortOrder]);

  // Resolve the currently-selected project (for the inline header above
  // the flows grid). null = Inbox or "not in a project view".
  const currentProject = useMemo(() => {
    if (!projectsEnabled || !selectedProjectParam || selectedProjectParam === INBOX_PROJECT_ID) {
      return null;
    }
    return projects.find((p) => p.id === selectedProjectParam) ?? null;
  }, [projectsEnabled, selectedProjectParam, projects]);

  // ── Render ──────────────────────────────────────────────────────
  // When projects are ON and no project is selected, show the project
  // grid. Otherwise show the flow grid.
  const showProjectsGrid = projectsEnabled && !selectedProjectParam;

  const handleSelectProject = (id: string | typeof INBOX_PROJECT_ID) => {
    setSearchParams({ project: id });
  };

  const handleBackToProjects = () => {
    setSearchParams({});
  };

  // The title above the flows grid when we're inside a project (or Inbox).
  // The default bucket renders as "General" everywhere user-facing,
  // even though the internal sentinel is still INBOX_PROJECT_ID.
  const flowGridTitle = projectsEnabled && selectedProjectParam
    ? (selectedProjectParam === INBOX_PROJECT_ID
        ? 'General'
        : currentProject?.name ?? 'Project')
    : (showArchived ? 'Archived' : 'My Knods');

  return (
    <div className={`dashboard${mounted ? ' dashboard--mounted' : ''}${warmStart ? ' dashboard--instant' : ''}`}>
      <div className="dashboard-main">
        <header className="dashboard-header">
          <div className="dashboard-header-left">
            {projectsEnabled && selectedProjectParam && (
              <button
                type="button"
                onClick={handleBackToProjects}
                aria-label="Back to projects"
                style={{
                  background: 'transparent', border: 'none', color: '#aaa',
                  cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center',
                  marginRight: 8,
                }}
              >
                <ArrowBackIcon style={{ fontSize: 22 }} />
              </button>
            )}
            <KnodsLogo className="dashboard-logo" />
            {projectsEnabled && selectedProjectParam && (
              <span style={{ marginLeft: 16, color: '#fff', fontSize: 16, fontWeight: 500 }}>
                {currentProject?.emoji && <span style={{ marginRight: 8 }}>{currentProject.emoji}</span>}
                {flowGridTitle}
              </span>
            )}
          </div>
          <div className="dashboard-header-right">
            <UserMenu creditsTooltipMode="buy-only" />
            <FlowUsageCounter
              used={flowCount}
              limit={flowLimit}
              loading={flowLimitLoading}
            />
            <button className="dashboard-upgrade-button" onClick={() => navigate('/pricing')}>
              Upgrade
            </button>
            <button className="dashboard-create-button" onClick={handleCreateNewKnod}>
              Create New Knod
            </button>
          </div>
        </header>

        {isMobile && !mobileBannerDismissed && (
          <div className="dashboard-mobile-banner">
            <div className="dashboard-mobile-banner-content">
              <p className="dashboard-mobile-banner-title">You're on mobile — nice!</p>
              <p className="dashboard-mobile-banner-text">
                Knods is built for desktop-first creative workflows. Mobile access is available
                so you can review, iterate, and generate on the go — but for the full canvas
                experience, grab your laptop.
              </p>
            </div>
            <button
              className="dashboard-mobile-banner-dismiss"
              onClick={() => {
                setMobileBannerDismissed(true);
                localStorage.setItem('knods-mobile-banner-dismissed', 'true');
              }}
              aria-label="Dismiss"
            >
              Got it
            </button>
          </div>
        )}

        {showProjectsGrid ? (
          <section className="dashboard-flows-section">
            <h1 className="dashboard-flows-title">Projects</h1>
            <div className="dashboard-flows-container">
              <ProjectGrid
                projects={projects.filter((p) => !p.archived)}
                loading={loading}
                flowCountByProject={projectGridDerivation.counts}
                coverByProject={projectGridDerivation.covers}
                onSelectProject={handleSelectProject}
                onCreateProject={openCreateProjectModal}
                onRenameProject={openRenameProjectModal}
                onDeleteProject={(p) => setDeleteProjectConfirm(p)}
                onArchiveProject={handleArchiveProject}
              />
            </div>
          </section>
        ) : (
          <FlowGrid
            flows={filteredFlows}
            loading={loading}
            showSkeletons={showSkeletons}
            isMobile={isMobile}
            viewMode={viewMode}
            searchQuery={searchQuery}
            showArchived={showArchived}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearchChange={setSearchQuery}
            onViewModeChange={setViewMode}
            onShowArchivedToggle={() => setShowArchived((v) => !v)}
            onSortByChange={setSortBy}
            onSortOrderToggle={() => setSortOrder((v) => (v === 'desc' ? 'asc' : 'desc'))}
            onOpenKnod={handleOpenKnod}
            onCloneKnod={handleCloneKnod}
            onRenameKnod={handleRenameFlow}
            onArchiveKnod={handleArchiveFlow}
            onDeleteKnod={handleDeleteFlow}
            onMoveKnodToProject={projectsEnabled ? (f) => setMoveTargetFlow(f) : undefined}
            selectionMode={selectionMode}
            selectedFlowIds={selectedFlowIds}
            onEnterSelection={projectsEnabled ? handleEnterSelection : undefined}
            onExitSelection={handleExitSelection}
            onToggleSelect={handleToggleSelect}
            onBulkMove={() => setBulkMoveOpen(true)}
            allVisibleSelected={
              filteredFlows.length > 0 &&
              filteredFlows.every((f) => selectedFlowIds.has(f.metadata.id))
            }
            onToggleSelectAll={() => {
              const allSelected = filteredFlows.every((f) => selectedFlowIds.has(f.metadata.id));
              setSelectedFlowIds(allSelected ? new Set() : new Set(filteredFlows.map((f) => f.metadata.id)));
            }}
            onStartTutorial={handleStartTutorial}
          />
        )}
      </div>

      {/* Tutorials panel — hidden entirely when Projects v1 is on. */}
      {!projectsEnabled && (
        <TutorialsPanel videos={DEFAULT_TUTORIAL_VIDEOS} onSelectVideo={setSelectedVideo} />
      )}

      {selectedVideo && (
        <YouTubeLightbox
          videoId={selectedVideo.youtubeId}
          title={selectedVideo.title}
          onClose={() => setSelectedVideo(null)}
        />
      )}

      {/* Project modals */}
      <ProjectModal
        open={projectModalMode !== null}
        mode={projectModalMode ?? 'create'}
        initial={projectModalInitial}
        onClose={() => { setProjectModalMode(null); setProjectBeingEdited(null); }}
        onSubmit={handleProjectModalSubmit}
      />

      <MoveToProjectModal
        open={moveTargetFlow !== null}
        currentProjectId={moveTargetFlow?.metadata.projectId ?? INBOX_PROJECT_ID}
        projects={projects.filter((p) => !p.archived)}
        onClose={() => setMoveTargetFlow(null)}
        onSelect={handleMoveSelect}
      />

      {/* Bulk-move modal — same picker, no "Current" affordance because
          a mixed selection might span multiple projects. */}
      <MoveToProjectModal
        open={bulkMoveOpen}
        currentProjectId={undefined}
        projects={projects.filter((p) => !p.archived)}
        onClose={() => setBulkMoveOpen(false)}
        onSelect={handleBulkMoveSelect}
        title={`Move ${selectedFlowIds.size} ${selectedFlowIds.size === 1 ? 'Knod' : 'Knods'}`}
      />

      {/* Delete project confirmation */}
      {deleteProjectConfirm && (
        <div className="delete-confirm-overlay" onClick={() => setDeleteProjectConfirm(null)}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete project?</h3>
            <p>
              Delete <strong>"{deleteProjectConfirm.name}"</strong>?
              Its {projectGridDerivation.counts[deleteProjectConfirm.id] ?? 0} Knods
              will move to General.
            </p>
            <div className="delete-confirm-actions">
              <button className="delete-confirm-cancel" onClick={() => setDeleteProjectConfirm(null)}>
                Cancel
              </button>
              <button className="delete-confirm-delete" onClick={confirmDeleteProject}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete flow confirmation */}
      {deleteConfirm && (
        <div className="delete-confirm-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete Flow?</h3>
            <p>Are you sure you want to delete <strong>"{deleteConfirm.metadata.name}"</strong>? This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button className="delete-confirm-cancel" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="delete-confirm-delete" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename flow */}
      {renameFlow && (
        <div className="delete-confirm-overlay" onClick={() => setRenameFlow(null)}>
          <div className="delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Rename Flow</h3>
            <input
              type="text"
              className="rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && confirmRename()}
              autoFocus
            />
            <div className="delete-confirm-actions">
              <button className="delete-confirm-cancel" onClick={() => setRenameFlow(null)}>
                Cancel
              </button>
              <button className="delete-confirm-delete rename-confirm" onClick={confirmRename}>
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
